import { useMemo, useState } from 'react';
import type { CSSProperties, JSX, ReactNode, UIEvent } from 'react';

import { Permission, formatGroupedNumber, formatRelativeFa } from '@bookora/shared';
import { Button, Code, Numeric, Phone, Select, Spin, StatusTag, useDebounced } from '@bookora/ui';
import type { PagedEnvelope } from '@bookora/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import type {
  PatientSummaryResponse,
  PatientsListParams,
  PatientsListSort,
} from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';
import { patientOption } from '@/components/patient-option';
import { listPatients } from '@/features/patients/api';
import { BLACKLIST_TAG } from '@/features/patients/labels';
import { usePatient } from '@/features/sessions/api';
import { useCan } from '@/permissions/use-can';

export interface PatientPickerProps {
  /** Optional so the picker can sit directly inside an AntD `Form.Item`. */
  value?: string | null;
  onChange?: (patientId: string | null) => void;
  placeholder?: string;
  ariaLabel?: string;
  style?: CSSProperties;
  disabled?: boolean;
  /** For a `<label htmlFor>` outside the control. */
  id?: string;
  /**
   * The chosen patient, when the caller already holds it — an appointment
   * carries its own. Used to label the value until (or instead of) the
   * `GET /patients/:id` the picker would otherwise need.
   */
  selected?: PickablePatient | null;
}

/**
 * «جست‌وجو و انتخاب بیمار» — ONE control: open it and pick, or type to narrow.
 *
 * `/sessions` and `/photos` each used to split this into a search box that did
 * nothing to the list on its own, next to a separate select that was empty
 * until something had been typed in the other box. The CRM loyalty tab went
 * further and asked for a raw patient UUID. This is the session drawer's
 * pattern, extracted.
 *
 * ── OPENING IT SHOWS PATIENTS ───────────────────────────────────────────────
 *
 * It used to open onto «نام، موبایل یا شمارهٔ پرونده رو بنویسید» and nothing
 * else. The people at a clinic's front desk read an empty list as «there are
 * no patients» or «it is broken», not as an invitation to type, and the
 * patient they want is nearly always one they just registered or one who was
 * in this week. So an empty search shows exactly those two groups:
 *
 *   · «تازه ثبت‌شده» — up to five files opened in the last fortnight. A new
 *     patient has no visit yet, so a list ordered by last visit alone buries
 *     the very file the receptionist created a minute ago (NULLs sort last).
 *   · «به ترتیب آخرین مراجعه» — everyone else, most recent visit first, loaded
 *     a page at a time as the popup is scrolled. It is the whole list, not a
 *     sample, and the footer says how much of it is showing.
 *
 * Nothing is requested until the popup first opens: this control sits on
 * pages that are usually used without it. The chosen patient is the one
 * exception — it is fetched on its own (via `usePatient`) so a value that
 * arrived from the URL shows a name, never an id.
 *
 * ── TYPING ──────────────────────────────────────────────────────────────────
 *
 * The server's name search matches from two letters (a prefix blind index),
 * so one letter would ask it a question it answers with nothing. One letter
 * therefore narrows what is already loaded, instantly, and from two on the
 * server searches the whole clinic — names, mobile, national ID, file code.
 * Persian digits need no folding here: `buildPatientWhere` folds them for the
 * code and national ID, and `normalizeMobile` for the phone.
 *
 * The query keys sit under `['patients', clinicId]`, so registering a patient
 * (which invalidates that root) refreshes an open picker too.
 */

const PAGE_SIZE = 20;
const NEW_PATIENTS_SHOWN = 5;
const NEW_PATIENT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
/** How close to the bottom of the popup the next page is requested, in px. */
const LOAD_AHEAD_PX = 64;

/** The fields an option row reads — the summary row, or the pinned detail. */
export interface PickablePatient {
  id: string;
  fullName: string;
  code?: string | null;
  phone?: string | null;
  lastVisitAt?: string | null;
  isBlacklisted?: boolean;
}

type PatientPages = InfiniteData<PagedEnvelope<PatientSummaryResponse>>;

function usePatientPages(
  params: Pick<PatientsListParams, 'q' | 'sort' | 'direction'>,
  pageSize: number,
  enabled: boolean,
) {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);

  return useInfiniteQuery({
    queryKey: ['patients', activeClinicId, 'picker', params, pageSize] as const,
    queryFn: ({ pageParam, signal }) =>
      listPatients({ ...params, page: pageParam, pageSize }, signal),
    initialPageParam: 1,
    getNextPageParam: (last: PagedEnvelope<PatientSummaryResponse>) =>
      last.page < last.pageCount ? last.page + 1 : undefined,
    enabled: enabled && canView && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

function rowsOf(data: PatientPages | undefined): PatientSummaryResponse[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

function totalOf(data: PatientPages | undefined): number | null {
  return data?.pages[0]?.total ?? null;
}

/** One letter, matched against what is already on screen. */
function matchesLocally(patient: PickablePatient, term: string): boolean {
  const needle = term.trim();
  if (needle === '') return true;
  return (
    patient.fullName.includes(needle) ||
    (patient.code ?? '').includes(needle) ||
    (patient.phone ?? '').includes(needle)
  );
}

function toOption(patient: PickablePatient) {
  // `title` is what AntD puts on the row's `title` attribute: the hover text
  // for a name the popup truncates, and the handle tests pick a row by.
  return { ...patientOption(patient), title: patient.fullName, patient };
}

type PickerOption = ReturnType<typeof toOption>;
interface PickerGroup {
  label: ReactNode;
  title: string;
  options: PickerOption[];
}

/**
 * The row in the popup: who, and enough to tell two «مریم احمدی»s apart. The
 * selected value keeps `patientOption`'s one-line label — the field is narrow,
 * the popup is not.
 */
function PatientOptionRow({ patient }: { patient: PickablePatient }): JSX.Element {
  const lastVisit = patient.lastVisitAt ? new Date(patient.lastVisitAt) : null;
  return (
    <div style={{ display: 'grid', gap: 2, paddingBlock: 2 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--bk-space-2)' }}>
        <span style={{ fontWeight: 500 }}>{patient.fullName}</span>
        {/* The same tag, wording and hint as the list, the file and the
            calendar — picking a restricted patient is exactly when the
            desk needs to know what the red chip means. */}
        {patient.isBlacklisted ? (
          <StatusTag tone={BLACKLIST_TAG.tone} icon={BLACKLIST_TAG.icon} hint={BLACKLIST_TAG.hint}>
            {BLACKLIST_TAG.label}
          </StatusTag>
        ) : null}
      </span>
      <span
        className="bk-subtle"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: 'var(--bk-space-3)',
          fontSize: 'var(--bk-text-caption)',
        }}
      >
        {patient.code ? (
          <span>
            پرونده <Code value={patient.code} persianDigits />
          </span>
        ) : null}
        {patient.phone ? <Phone value={patient.phone} /> : null}
        <span>
          {lastVisit ? (
            <>
              آخرین مراجعه{' '}
              <Numeric as="time" dir="auto" tabular={false} dateTime={lastVisit.toISOString()}>
                {formatRelativeFa(lastVisit)}
              </Numeric>
            </>
          ) : (
            'هنوز مراجعه نکرده'
          )}
        </span>
      </span>
    </div>
  );
}

function groupLabel(text: string): JSX.Element {
  return <span className="bk-subtle">{text}</span>;
}

export function PatientPicker({
  value: rawValue,
  onChange,
  placeholder = 'جست‌وجو و انتخاب بیمار',
  ariaLabel = 'جست‌وجو و انتخاب بیمار',
  style,
  disabled,
  id,
  selected = null,
}: PatientPickerProps): JSX.Element {
  // A form may hold '' for «nothing chosen»; to the picker that is no value.
  const value = rawValue ? rawValue : null;
  const [term, setTerm] = useState('');
  // Sticky: once the popup has been opened the lists stay warm, so closing and
  // reopening it does not flash an empty popup.
  const [opened, setOpened] = useState(false);
  const debounced = useDebounced(term, 300).trim();
  const serverSearch = debounced.length >= 2;
  const typing = term.trim().length > 0;

  const recentlyVisited = usePatientPages(
    { sort: 'lastVisitAt' satisfies PatientsListSort, direction: 'desc' },
    PAGE_SIZE,
    opened,
  );
  const recentlyRegistered = usePatientPages(
    { sort: 'createdAt' satisfies PatientsListSort, direction: 'desc' },
    NEW_PATIENTS_SHOWN,
    opened,
  );
  const found = usePatientPages({ q: debounced }, PAGE_SIZE, serverSearch);
  const pinned = usePatient(value);

  // Whichever list is on screen is the one the popup's scroll pages through.
  const active = serverSearch ? found : recentlyVisited;

  const options = useMemo((): Array<PickerOption | PickerGroup> => {
    const current: PickablePatient | null =
      pinned.data ?? (selected && selected.id === value ? selected : null);

    if (serverSearch) {
      const rows: PickablePatient[] = rowsOf(found.data);
      const merged =
        current && !rows.some((row) => row.id === current.id) ? [current, ...rows] : rows;
      return merged.map(toOption);
    }

    // `Date.now()` in a memo is fine: the window is a fortnight, and the memo
    // is rebuilt whenever the lists refetch.
    const cutoff = Date.now() - NEW_PATIENT_WINDOW_MS;
    const fresh = rowsOf(recentlyRegistered.data)
      .filter((row) => new Date(row.createdAt).getTime() >= cutoff)
      .filter((row) => matchesLocally(row, term));
    const freshIds = new Set(fresh.map((row) => row.id));
    const visited = rowsOf(recentlyVisited.data)
      .filter((row) => !freshIds.has(row.id))
      .filter((row) => matchesLocally(row, term));

    const groups: PickerGroup[] = [];
    const listed =
      current && (freshIds.has(current.id) || visited.some((row) => row.id === current.id));
    if (current && !listed) {
      groups.push({
        label: groupLabel('انتخاب فعلی'),
        title: 'انتخاب فعلی',
        options: [toOption(current)],
      });
    }
    if (fresh.length > 0) {
      groups.push({
        label: groupLabel('تازه ثبت‌شده'),
        title: 'تازه ثبت‌شده',
        options: fresh.map(toOption),
      });
    }
    if (visited.length > 0) {
      groups.push({
        label: groupLabel('به ترتیب آخرین مراجعه'),
        title: 'به ترتیب آخرین مراجعه',
        options: visited.map(toOption),
      });
    }
    return groups;
  }, [
    serverSearch,
    found.data,
    recentlyRegistered.data,
    recentlyVisited.data,
    pinned.data,
    selected,
    value,
    term,
  ]);

  const firstLoad =
    active.isLoading || (serverSearch && found.isFetching && !found.isFetchingNextPage);
  const pendingSearch = typing && term.trim() !== debounced && term.trim().length >= 2;

  function onPopupScroll(event: UIEvent<HTMLDivElement>): void {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight < target.scrollHeight - LOAD_AHEAD_PX) return;
    if (active.hasNextPage && !active.isFetchingNextPage) void active.fetchNextPage();
  }

  const shown = rowsOf(active.data).length;
  const total = totalOf(active.data);

  const footer: ReactNode = active.isFetchingNextPage ? (
    <FooterNote>
      <Spin size="small" /> در حال بارگذاری بیماران بیشتر…
    </FooterNote>
  ) : active.hasNextPage ? (
    <FooterNote>
      <Button
        type="link"
        size="small"
        style={{ paddingInline: 0 }}
        // Keep the popup open: a click would otherwise blur the input first.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => void active.fetchNextPage()}
      >
        نمایش بیشتر
      </Button>
      {total !== null && !serverSearch && !typing ? (
        // No «·» before the count: beside Persian digits it reads as «۰», and
        // «· ۲۰ از ۱۵۱» was read as «۲۰۰ از ۱۵۱». The flex gap separates it
        // from the button; see `components/meta-line.tsx`.
        <span>
          <Numeric>{formatGroupedNumber(shown)}</Numeric> از{' '}
          <Numeric>{formatGroupedNumber(total)}</Numeric> بیمار نمایش داده شده — برای یافتن سریع‌تر،
          نام یا شمارهٔ پرونده رو بنویسید.
        </span>
      ) : null}
    </FooterNote>
  ) : null;

  const emptyContent =
    firstLoad || pendingSearch ? (
      <span role="status" style={{ display: 'inline-flex', gap: 'var(--bk-space-2)' }}>
        <Spin size="small" />
        <span className="bk-subtle">{typing ? 'در حال جست‌وجو…' : 'در حال بارگذاری بیماران…'}</span>
      </span>
    ) : active.isError ? (
      'فهرست بیماران بارگذاری نشد. کادر انتخاب بیمار رو ببندید و دوباره باز کنید.'
    ) : typing ? (
      term.trim().length < 2 ? (
        'برای جست‌وجو در همهٔ بیماران، یک حرف دیگه بنویسید.'
      ) : (
        'بیماری با این نام، موبایل یا شمارهٔ پرونده پیدا نشد.'
      )
    ) : (
      'هنوز بیماری ثبت نشده.'
    );

  return (
    <Select
      id={id}
      showSearch
      allowClear
      filterOption={false}
      value={value}
      searchValue={term}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      // Never wider than its container: a fixed 260px minimum overflowed a
      // phone-width drawer by the width of the clear icon.
      style={{ minWidth: 'min(260px, 100%)', ...style }}
      // The popup carries two-line rows; the field's width would squeeze them.
      popupMatchSelectWidth={false}
      styles={{ popup: { root: { minWidth: 'min(360px, 100vw - 32px)' } } }}
      listHeight={320}
      options={options}
      optionRender={(option) =>
        option.data.patient ? <PatientOptionRow patient={option.data.patient} /> : option.label
      }
      onOpenChange={(open) => {
        if (open) setOpened(true);
      }}
      onPopupScroll={onPopupScroll}
      popupRender={(menu) => (
        <>
          {menu}
          {options.length > 0 ? footer : null}
        </>
      )}
      onSearch={(next) => {
        setTerm(next);
        setOpened(true);
      }}
      onChange={(next) => {
        onChange?.(next ?? null);
        setTerm('');
      }}
      notFoundContent={emptyContent}
    />
  );
}

function FooterNote({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      className="bk-subtle"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'var(--bk-space-2)',
        padding: 'var(--bk-space-2) var(--bk-space-3)',
        borderBlockStart: '1px solid var(--bk-border-subtle)',
        fontSize: 'var(--bk-text-caption)',
      }}
    >
      {children}
    </div>
  );
}
